import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { jsx } from 'react/jsx-runtime';
import { RequestType, KameleoonUtils, KameleoonClient } from '@kameleoon/javascript-sdk';

const FeatureFlagContext = /*#__PURE__*/ createContext(undefined);
const useFeatureFlagContext = ()=>{
    const context = useContext(FeatureFlagContext);
    if (context === undefined) {
        throw new Error("useFeatureFlagContext must be used within a FeatureFlaggingProvider");
    }
    return context;
};

/**
 * Provides feature flagging utilities and state from the FeatureFlag context.
 *
 * If the feature flag variation is off, it returns off instead of default value.
 * If consent is false, it returns the default value.
 * If kameleoon as any issue, it returns the default value.
 *
 * @returns {FeatureFlaggingHookReturnType} An object containing:
 *   - getFeatureFlags: function to get all feature flags and their active status for the current visitor (debugging purposes)
 *   - isFeatureFlagActive: function to check if a specific feature flag is active for the current visitor (should only be used with on / off feature flags)
 *   - getVariation: function to get the variation for a feature flag (returns the variation key or default value)
 *   - isLoaded: boolean indicating if the feature flagging system is fully loaded and ready
 *   - context: object containing siteCode, environment, and visitorCode (debugging purposes)
 */ function useFeatureFlagging() {
    const context = useFeatureFlagContext();
    const getFeatureFlags = ()=>{
        // still loading or waiting for consent
        if (!context.initialized) {
            return [];
        }
        // no Kameleoon client available, fallback to an empty object
        // should not happen if the provider is used correctly
        if (!context.kameleoonClient) {
            return [];
        }
        if (!context.visitorCode) {
            return [];
        }
        try {
            const featureFlags = context.kameleoonClient.getVariations({
                visitorCode: context.visitorCode ?? "",
                onlyActive: true
            });
            return Array.from(featureFlags.entries()).map(([key, _flag])=>({
                    key,
                    active: Boolean(context.kameleoonClient?.isFeatureFlagActive(context.visitorCode ?? "", key))
                }));
        } catch (error) {
            console.warn("Error getting feature flags:", error);
            return [];
        }
    };
    const getVariation = (featureKey, defaultValue)=>{
        // still loading or waiting for consent
        if (!context.initialized) {
            return undefined;
        }
        if (context.featureFlagConsent === false) {
            return defaultValue;
        }
        if (!context.kameleoonClient) {
            console.warn("[getVariation] No Kameleoon client available, returning default value.");
            return defaultValue;
        }
        const visitorCode = context.visitorCode;
        if (!visitorCode) {
            console.warn("[getVariation] No visitor code available, returning default value.");
            return defaultValue;
        }
        try {
            // check if the feature flag is active for the visitor
            const variationKey = context.kameleoonClient.getVariation({
                featureKey,
                visitorCode
            })?.key ?? defaultValue;
            return variationKey;
        } catch (error) {
            // If there's an error getting the variation, log it and return the default value
            console.warn(`Error getting variation for feature ${featureKey} for visitor ${visitorCode}, fallback to default value:`, error);
            return defaultValue;
        }
    };
    const isFeatureFlagActive = (featureKey)=>{
        // still loading or waiting for consent
        if (!context.initialized) {
            return false;
        }
        if (!context.featureFlagConsent) {
            return false;
        }
        if (!context.kameleoonClient) {
            console.warn("[isFeatureFlagActive] No Kameleoon client available, returning false.");
            return false;
        }
        const visitorCode = context.visitorCode;
        if (!visitorCode) {
            console.warn("[isFeatureFlagActive] No visitor code available, returning false.");
            return false;
        }
        try {
            return context.kameleoonClient.isFeatureFlagActive(visitorCode, featureKey);
        } catch (error) {
            // If there's an error checking the feature flag, log it and return false
            console.warn(`Error checking feature flag ${featureKey} for visitor ${visitorCode}, returning false:`, error);
            return false;
        }
    };
    return {
        getFeatureFlags,
        getVariation,
        isFeatureFlagActive,
        isLoaded: context.initialized && !context.loading,
        context: {
            siteCode: context.parameters.siteCode,
            environment: context.parameters.environment,
            visitorCode: context.visitorCode
        }
    };
}

const KAMELEOON_ENGINE_SCRIPT_IDENTIFIER = "kameleoon-engine-script";
async function loadKameleoonEngine(siteCode) {
    return new Promise((resolve)=>{
        if (document.querySelector(`#${KAMELEOON_ENGINE_SCRIPT_IDENTIFIER}`)) {
            resolve();
        }
        const kameleoonEngineScript = document.createElement("script");
        kameleoonEngineScript.id = KAMELEOON_ENGINE_SCRIPT_IDENTIFIER;
        kameleoonEngineScript.src = `//${siteCode}.kameleoon.io/engine.js`;
        kameleoonEngineScript.fetchPriority = "high";
        kameleoonEngineScript.async = true;
        kameleoonEngineScript.addEventListener("load", ()=>{
            resolve();
        });
        document.head.insertBefore(kameleoonEngineScript, document.head.firstElementChild);
    });
}
function setEngineVisitorCode(visitorCode) {
    window.kameleoonQueue = window.kameleoonQueue || [];
    window.kameleoonQueue.push({
        level: "IMMEDIATE",
        command: ()=>window.Kameleoon?.API.Visitor.setVisitorCode(visitorCode)
    });
}
function setEngineLegalConsent(consent) {
    window.kameleoonQueue = window.kameleoonQueue || [];
    const command = consent ? ()=>window.Kameleoon?.API.Core.enableLegalConsent() : ()=>window.Kameleoon?.API.Core.disableLegalConsent();
    window.kameleoonQueue.push({
        level: "IMMEDIATE",
        command
    });
}

class MockTrackingRequester {
    async sendRequest({ url, parameters, requestType }) {
        if (requestType === RequestType.Tracking) {
            return KameleoonUtils.simulateSuccessRequest(requestType, null);
        }
        return fetch(url, parameters);
    }
}

const MAX_TIMEOUT = 400; // ms, adjust as needed
// Map internal environment to Kameleoon environment
const ENVIRONMENT_MAP = {
    live: "production",
    preview: "staging",
    dev: "development"
};
/**
 * FeatureFlaggingProvider wraps children with feature flag context and manages the loading and initialization
 * of the Kameleoon client based on user consent and context. It provides loading state management, client instance
 * initialization, consent handling, and timeout management for robust feature flag functionality.
 *
 * The provider handles:
 * - Automatic Kameleoon client initialization when technical consent is given
 * - Timeout management with configurable max timeout (defaults to 400ms)
 * - Consent management for both technical and tracking purposes
 * - Loading state management during initialization
 * - SSR safety by checking for browser environment before client creation
 * - Visitor code generation and management
 * - Mock tracking support for testing environments
 *
 * @param props.children - React children to be wrapped by the provider.
 * @param props.context - Feature flag context properties (optional).
 *   @property getTrackingUserConsent - Function returning whether the user has given tracking consent (boolean or undefined).
 *   @property getTechnicalUserConsent - Function returning whether the user has given technical consent (boolean or undefined).
 *   @property maxTimeout - Optional timeout in ms before initialization is considered complete (defaults to 400ms).
 *   @property mockTracking - Optional boolean to enable mock tracking for testing (defaults to false).
 *   @property parameters - Object containing configuration parameters for the feature flagging system.
 *     @property siteCode - The unique code identifying the site or application instance.
 *     @property environment - The environment in which the application is running ('live', 'preview', or 'dev').
 *     @property domain - Optional domain for the Kameleoon client configuration.
 * @returns JSX.Element - The provider component wrapping children with feature flag context, or just children if no context is provided.
 */ const FeatureFlaggingProvider = ({ children, context })=>{
    const [initialized, setInitialized] = useState(false);
    const [kameleoonClient, setKameleoonClient] = useState();
    const [visitorCode, setVisitorCode] = useState();
    const [featureFlagConsent, setFeatureFlagConsent] = useState(false);
    const [loading, setLoading] = useState(false);
    const userTrackingConsent = context?.getTrackingUserConsent();
    const userTechnicalConsent = context?.getTechnicalUserConsent();
    // Set maxTimeout to context value or fallback to MAX_TIMEOUT
    const maxTimeout = context?.maxTimeout ?? MAX_TIMEOUT;
    useEffect(()=>{
        if (!context || !userTechnicalConsent || kameleoonClient) {
            return;
        }
        setLoading(true);
        const timeoutId = setTimeout(()=>{
            console.warn(`Kameleoon client initialization timed out after ${maxTimeout}ms.`);
            setLoading(false);
            setInitialized(true);
        }, maxTimeout);
        async function initializeKameleoonClient() {
            if (!context || !userTechnicalConsent || kameleoonClient) return;
            await loadKameleoonEngine(context.parameters.siteCode);
            const newkameleoonClient = new KameleoonClient({
                siteCode: context.parameters.siteCode,
                configuration: {
                    environment: ENVIRONMENT_MAP[context.parameters.environment],
                    cookieDomain: context.parameters.domain ?? ""
                },
                ...context.mockTracking && {
                    externals: {
                        requester: new MockTrackingRequester()
                    }
                }
            });
            setKameleoonClient(newkameleoonClient);
            await newkameleoonClient.initialize();
            clearTimeout(timeoutId);
            setInitialized(true);
            setLoading(false);
        }
        initializeKameleoonClient();
        return ()=>{
            clearTimeout(timeoutId);
            setLoading(false);
        };
    }, [
        context,
        kameleoonClient,
        userTechnicalConsent,
        maxTimeout
    ]);
    // update consent in Kameleoon if it exists
    useEffect(()=>{
        if (kameleoonClient?.isInitialized() && initialized && userTrackingConsent !== undefined) {
            setFeatureFlagConsent(Boolean(userTrackingConsent));
            setEngineLegalConsent(Boolean(userTrackingConsent));
            if (userTrackingConsent) {
                const visitorCode = kameleoonClient.getVisitorCode();
                kameleoonClient.setLegalConsent(visitorCode, Boolean(userTrackingConsent));
                setVisitorCode(visitorCode);
                setEngineVisitorCode(visitorCode);
            }
            if (!userTrackingConsent && visitorCode) {
                kameleoonClient.setLegalConsent(visitorCode, Boolean(userTrackingConsent));
            }
        }
    }, [
        kameleoonClient,
        userTrackingConsent,
        visitorCode,
        initialized
    ]);
    const featureFlagContextValue = useMemo(()=>{
        if (!context) {
            return;
        }
        return {
            kameleoonClient,
            visitorCode,
            initialized,
            featureFlagConsent,
            loading,
            parameters: {
                siteCode: context.parameters.siteCode,
                environment: ENVIRONMENT_MAP[context.parameters.environment],
                domain: context.parameters.domain
            }
        };
    }, [
        context,
        kameleoonClient,
        visitorCode,
        initialized,
        featureFlagConsent,
        loading
    ]);
    return featureFlagContextValue ? /*#__PURE__*/ jsx(FeatureFlagContext.Provider, {
        value: featureFlagContextValue,
        children: children
    }) : children;
};

/**
 * Technical consent IDs for Kameleoon by brand code.
 *
 * @remarks
 * Used to map each Aviv brand to its technical consent ID for Kameleoon integration.
 *
 * @example
 * import { KAMELEOON_TECHNICAL_CONSENT_IDS } from "@aviv-trak/feature-flagging";
 * const consentId = KAMELEOON_TECHNICAL_CONSENT_IDS[brand];
 */ const KAMELEOON_TECHNICAL_CONSENT_IDS = {
    iwb: "NePrig_tiu-jvm",
    imn: "XmNtj2TX-35Fu-",
    iwt: "XmNtj2TX-35Fu-",
    iwtat: "XmNtj2TX-35Fu-",
    li: "GnFcLZLt7C-8X6",
    sl: "GnFcLZLt7C-8X6",
    sln: "GnFcLZLt7C-8X6",
    slp: "GnFcLZLt7C-8X6"
};
/**
 * Consent IDs for Kameleoon by brand code.
 *
 * @remarks
 * Used to map each Aviv brand to its consent ID for Kameleoon integration.
 *
 * @example
 * import { KAMELEOON_CONSENT_IDS } from "@aviv-trak/feature-flagging";
 * const consentId = KAMELEOON_CONSENT_IDS[brand];
 */ const KAMELEOON_CONSENT_IDS = {
    imn: "9FYjH37-J",
    iwb: "9FYjH37-J",
    iwt: "9FYjH37-J",
    iwtat: "9FYjH37-J",
    li: "9FYjH37-J",
    sl: "9FYjH37-J",
    sln: "9FYjH37-J",
    slp: "9FYjH37-J"
};
/**
 * Mapping of brand codes to their respective Kameleoon site codes.
 *
 * @remarks
 * Each brand in the Aviv group has a unique Kameleoon site code, which is required for SDK initialization.
 * Use the appropriate code based on the current brand context.
 *
 * @example
 * import { KAMELEOON_BRAND_SITE_CODE } from "@aviv-trak/feature-flagging";
 * const siteCode = KAMELEOON_BRAND_SITE_CODE[brand];
 */ const KAMELEOON_BRAND_SITE_CODE = {
    imn: "sqdhq8rbkk",
    iwb: "hrkn8tz1d4",
    iwt: "lrj80dsya2",
    iwtat: "d9zeeh3jut",
    li: "c13uk9geze",
    sl: "w9cce6c1xe",
    sln: "je6r4dy952",
    slp: "m6aztn5nin"
};

export { FeatureFlaggingProvider, KAMELEOON_BRAND_SITE_CODE, KAMELEOON_CONSENT_IDS, KAMELEOON_TECHNICAL_CONSENT_IDS, useFeatureFlagging };
